import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ClipboardList, Loader2, Upload, QrCode, ShieldCheck } from "lucide-react";
import api from "@/lib/api";

const EASE = [0.22, 1, 0.36, 1];
const BATTING = ["Right-hand bat", "Left-hand bat"];
const BOWLING = ["Right-arm fast", "Right-arm medium", "Right-arm spin", "Left-arm fast", "Left-arm medium", "Left-arm spin", "None"];
const SIZES = ["S", "M", "L", "XL", "XXL", "XXXL"];
const FEE = 2500;

const fieldCls = "w-full px-4 py-3 rounded-lg border border-black/10 bg-white text-sm text-league-ink placeholder:text-league-ink/40 focus:outline-none focus:ring-2 focus:ring-league-blue";

function Field({ label, children, testid }) {
  return (
    <div>
      <label className="block text-xs font-bold uppercase tracking-widest text-league-ink/60 mb-1.5">{label}</label>
      {children}
    </div>
  );
}

export default function Register() {
  const [form, setForm] = useState({
    player_name: "", email: "", whatsapp: "", cricheroes_link: "",
    batting_style: "Right-hand bat", bowling_style: "None", jersey_size: "L",
    jersey_name: "", jersey_number: "",
  });
  const [photo, setPhoto] = useState(null);
  const [paymentProof, setPaymentProof] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!paymentProof) {
      toast.error("Upload your payment screenshot first — scan the QR and pay the fee");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (photo) fd.append("photo", photo);
      fd.append("payment_proof", paymentProof);
      await api.post("/registrations", fd);
      setDone(true);
      toast.success("Registration received — see you at the auction!");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : Array.isArray(d) ? d.map((x) => x.msg).join(" ") : "Couldn't submit right now.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="pt-28 pb-24 bg-league-mist min-h-screen" data-testid="register-page">
      <div className="max-w-3xl mx-auto px-5 sm:px-8">
        <span className="text-[11px] font-bold uppercase tracking-[0.3em] text-league-blue flex items-center gap-2">
          <ClipboardList size={14} /> Season 6.0 · 19–21 Feb 2027
        </span>
        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-league-ink tracking-tighter mt-3 mb-4">
          PLAYER <span className="text-outline">REGISTRATION</span>
        </h1>
        <p className="text-muted-foreground mb-12 leading-relaxed">
          Want your name in the ASL 6.0 auction pool? Fill this in — franchises scout from this list.
        </p>

        {done ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, ease: EASE }}
            className="bg-white border border-black/10 rounded-2xl p-10 text-center"
            data-testid="register-success"
          >
            <p className="font-display text-3xl text-league-ink">YOU'RE IN THE POOL</p>
            <p className="text-muted-foreground text-sm mt-3">Your payment screenshot is with the league team — once the ₹{FEE.toLocaleString("en-IN")} fee is verified, you'll get a confirmation email and WhatsApp message with auction details.</p>
          </motion.div>
        ) : (
          <motion.form
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, ease: EASE }}
            onSubmit={submit}
            className="bg-white border border-black/10 rounded-2xl p-6 sm:p-10 space-y-5 shadow-[0_20px_50px_-20px_rgba(0,74,173,0.2)]"
            data-testid="register-form"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Player name">
                <input required value={form.player_name} onChange={set("player_name")} className={fieldCls} data-testid="reg-name" placeholder="Full name" />
              </Field>
              <Field label="Email address">
                <input required type="email" value={form.email} onChange={set("email")} className={fieldCls} data-testid="reg-email" placeholder="you@example.com" />
              </Field>
              <Field label="WhatsApp number">
                <input required value={form.whatsapp} onChange={set("whatsapp")} className={fieldCls} data-testid="reg-whatsapp" placeholder="+91 …" />
              </Field>
              <Field label="CricHeroes profile link">
                <input value={form.cricheroes_link} onChange={set("cricheroes_link")} className={fieldCls} data-testid="reg-cricheroes" placeholder="https://cricheroes.com/profile/…" />
              </Field>
              <Field label="Batting style">
                <select value={form.batting_style} onChange={set("batting_style")} className={fieldCls} data-testid="reg-batting">
                  {BATTING.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </Field>
              <Field label="Bowling style">
                <select value={form.bowling_style} onChange={set("bowling_style")} className={fieldCls} data-testid="reg-bowling">
                  {BOWLING.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </Field>
              <Field label="Jersey size">
                <select value={form.jersey_size} onChange={set("jersey_size")} className={fieldCls} data-testid="reg-size">
                  {SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="Name on jersey">
                <input value={form.jersey_name} onChange={set("jersey_name")} className={fieldCls} data-testid="reg-jersey-name" placeholder="e.g. KANAK" />
              </Field>
              <Field label="Number on jersey">
                <input value={form.jersey_number} onChange={set("jersey_number")} className={fieldCls} data-testid="reg-jersey-number" placeholder="e.g. 7" />
              </Field>
              <Field label="Your photo (for auctions)">
                <label className="flex items-center gap-3 px-4 py-3 rounded-lg border-2 border-dashed border-league-blue/30 text-sm text-league-ink/60 cursor-pointer transition-colors hover:border-league-blue" data-testid="reg-photo-label">
                  <Upload size={16} className="text-league-blue shrink-0" />
                  <span className="truncate">{photo ? photo.name : "Choose photo…"}</span>
                  <input type="file" accept="image/*" className="hidden" data-testid="reg-photo" onChange={(e) => setPhoto(e.target.files[0] || null)} />
                </label>
              </Field>
            </div>
            <div className="rounded-xl border-2 border-league-blue/20 bg-league-blue/5 p-5 sm:p-6" data-testid="reg-payment-section">
              <p className="text-xs font-bold uppercase tracking-widest text-league-blue flex items-center gap-2 mb-4">
                <QrCode size={15} /> Step 2 — Registration fee ₹{FEE.toLocaleString("en-IN")}
              </p>
              <div className="flex flex-col sm:flex-row gap-5 sm:items-center">
                <div className="bg-white rounded-xl p-3 border border-black/10 w-fit mx-auto sm:mx-0 shrink-0">
                  <img src="/payment-qr.png" alt="All Star League payment QR code" className="w-44 h-44 object-contain" data-testid="reg-payment-qr" />
                </div>
                <div className="text-sm text-league-ink/70 leading-relaxed">
                  <p className="font-semibold text-league-ink">Scan with any UPI app — GPay, PhonePe, Paytm.</p>
                  <p className="mt-1.5">Pay <span className="font-bold text-league-blue">₹{FEE.toLocaleString("en-IN")}</span>, take a screenshot of the successful payment, and upload it below. Your spot in the auction pool is confirmed once we verify it.</p>
                  <label className="mt-4 flex items-center gap-3 px-4 py-3 rounded-lg border-2 border-dashed border-league-blue/40 bg-white text-sm text-league-ink/60 cursor-pointer transition-colors hover:border-league-blue" data-testid="reg-payment-proof-label">
                    <Upload size={16} className="text-league-blue shrink-0" />
                    <span className="truncate">{paymentProof ? paymentProof.name : "Upload payment screenshot…"}</span>
                    <input type="file" accept="image/*" className="hidden" data-testid="reg-payment-proof" onChange={(e) => setPaymentProof(e.target.files[0] || null)} />
                  </label>
                  {paymentProof && (
                    <p className="mt-2 text-xs font-semibold text-emerald-600 flex items-center gap-1.5" data-testid="reg-payment-proof-ready">
                      <ShieldCheck size={13} /> Screenshot attached
                    </p>
                  )}
                </div>
              </div>
            </div>

            <button
              type="submit"
              disabled={busy}
              data-testid="reg-submit"
              className="w-full py-4 rounded-lg bg-league-blue text-white font-bold text-sm uppercase tracking-wider transition-colors duration-200 hover:bg-league-deep disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {busy && <Loader2 size={16} className="animate-spin" />}
              Pay ₹{FEE.toLocaleString("en-IN")} & register
            </button>
          </motion.form>
        )}
      </div>
    </main>
  );
}
